// MapNodeView.js — Thin orchestrator: chrome + fragment store + shared preview queue.
//
// Ticket 05: this view no longer owns a MapPreviewEngine (one live OL Map per node). Instead it
// enqueues refresh requests into a single shared MapPreviewQueueService (one live OL Map for the
// whole document) and renders whatever snapshot PNG ends up in the Yjs mapPreviews Y.Map — same
// as before. If no previewQueue is supplied (e.g. an older caller), the node view still works: it
// just never gets a rendered preview and stays on the placeholder, since there's nothing to
// enqueue into.

import { MapNodeChrome } from './MapNodeChrome';
import { MapFragmentStore } from './MapFragmentStore';
import { openMapEditor } from './MapSession';
import { resolveActiveViewId, resolveDisplayViewId, resolveViewBbox, viewFitKey } from './schema/mapPmAdapter';
import { getMapPreview, observeMapPreview } from './mapPreviewsYMap';
import { buildSitrepPayload } from './sitrep/buildSitrepPayload';
import { reverseGeocode, bboxCenter, geocodeUnits } from './sitrep/reverseGeocode';
import { queueSitrepGeneration, observeSitrepGeneration, getSitrepGeneration, SITREP_GENERATION_STATUS } from './sitrep/sitrepGenerationsYMap';
import { getSitrepProgressViewState } from './sitrep/sitrepProgressState';
import { generateUUID } from '../prosemirror-editor/helpers/utils/ids';

// Ticket MINSKY-16407: when the sitrep button is clicked and no preview snapshot exists yet for
// this node, we kick off a fresh capture (MapPreviewQueueService.requestRefresh) and wait a
// bounded amount of time for it to land in Yjs before building the payload anyway — the preview
// image is a nice-to-have for the LLM-facing payload, not something worth blocking the button
// indefinitely for for (e.g. if the shared preview engine is mid-queue behind other nodes).
const SITREP_PREVIEW_WAIT_MS = 4000;

export class MapNodeView {
    /**
     * @param {import('prosemirror-model').Node} node
     * @param {import('prosemirror-view').EditorView} view
     * @param {() => number|null} getPos
     * @param {object} opts
     * @param {object|null} opts.classificationMenu
     * @param {(nodeId: string) => import('yjs').XmlFragment|null} opts.getFragment
     * @param {(request: import('./MapSession').MapEditorSessionRequest) => void} [opts.onOpenEditor]
     * @param {import('yjs').Doc|null} [opts.ydoc]
     * @param {import('yjs').Map|null} [opts.yMapPreviews]
     * @param {import('yjs').Map|null} [opts.yMapSitrepGenerations]
     * @param {import('./MapPreviewQueueService').MapPreviewQueueService|null} [opts.previewQueue]
     */
    constructor(node, view, getPos, opts = {}) {
        this.node = node;
        this.view = view;
        this.getPos = getPos;
        this._onOpenEditor = opts.onOpenEditor || openMapEditor;
        this._ydoc = opts.ydoc || null;
        this._yMapPreviews = opts.yMapPreviews || null;
        this._yMapSitrepGenerations = opts.yMapSitrepGenerations || null;
        this._previewQueue = opts.previewQueue || null;
        this._getFragment = opts.getFragment || (() => null);
        this._destroyed = false;
        this._visible = false;
        this._refreshScheduled = false;
        this._pendingFitBounds = false;
        this._lastFitKey = undefined;
        this._unobservePreview = null;
        this._lastObservedNonZeroSize = false;

        // MINSKY-16410: local sitrep-progress state — `_sitrepInFlight` drives the header
        // trigger's disabled state (see `_setSitrepInFlight`/MapNodeChrome.updateSitrepInFlight);
        // `_lastSitrepParams` caches the last submitted {additionalContext, windowDays,
        // maxCheckpoints} so the under-the-map Retry action can resubmit without the user
        // reopening the modal and retyping their additional-context text.
        this._sitrepInFlight = false;
        this._lastSitrepParams = null;

        this._store = new MapFragmentStore(this._getFragment);
        this._chrome = new MapNodeChrome({
            classificationMenu: opts.classificationMenu || null,
            onViewSelected: (viewId) => this._onViewSelected(viewId),
            onDtgSelected: (dtgSelectedMs) => this._onDtgSelected(dtgSelectedMs),
            onBasemapSelected: (basemapStyleId) => this._onBasemapSelected(basemapStyleId),
            onEdit: () => this._openEditor(),
            onClassificationChange: (result) => this._onClassificationChange(result),
            onCaptionChange: (caption) => this._onCaptionChange(caption),
            onGenerateSitrep: (params) => this._onGenerateSitrep(params),
        });

        // Placeholder — shown while no snapshot has been captured yet.
        this._placeholder = document.createElement('div');
        this._placeholder.className = 'pm-map-node__snapshot-placeholder';
        this._placeholder.style.cssText = [
            'position:absolute',
            'inset:0',
            'width:100%',
            'height:100%',
            'display:flex',
            'align-items:center',
            'justify-content:center',
            'background:#f1f5f9',
            'color:#94a3b8',
            'font-size:0.75rem',
            'pointer-events:none',
        ].join(';');
        this._placeholder.textContent = 'Generating map preview…';
        this._chrome.mapContainer.style.position = 'relative';
        this._chrome.mapContainer.appendChild(this._placeholder);

        // Snapshot overlay — the sole user-visible element for the map embed.
        this._snapshotImg = document.createElement('img');
        this._snapshotImg.className = 'pm-map-node__snapshot-overlay';
        this._updateSnapshotAlt(node.attrs.caption);
        this._snapshotImg.style.cssText = [
            'position:absolute',
            'inset:0',
            'width:100%',
            'height:100%',
            'object-fit:cover',
            'pointer-events:none',
            'display:none',
        ].join(';');
        this._chrome.mapContainer.appendChild(this._snapshotImg);

        // Ticket 10: queue-pending badge — surfaces MapPreviewQueueService's intentional
        // single-shared-engine FIFO/priority serialization (see that file's header comment) so a
        // node sitting queued behind others under load (e.g. scrolling a report with many map
        // embeds) reads as "queued"/"rendering" rather than looking like a stalled/broken
        // refresh. Sits above both the placeholder and the snapshot overlay since a re-render can
        // be queued/active while a *previous* snapshot is still showing. No dedicated indicator
        // existed yet for this in the working tree (checked for ticket 06's "refreshing on
        // view/DTG change" indicator before adding this — see MapNodeView/MapNodeChrome — it
        // hadn't landed), so this is intentionally minimal; ticket 06 should reuse this badge
        // for its own pending/refreshing state instead of adding a second overlay.
        //
        // Ticket 05 reuses this same element for a distinct 'error' state (see
        // `_updateQueueBadge`/`_setQueueBadgeVariant`) — background/color/cursor are applied per
        // variant there rather than baked into this base cssText, so the pending ("queued"/
        // "rendering") look stays unchanged while the error variant gets its own color and becomes
        // clickable (a manual retry affordance, no auto-retry loop).
        this._queueBadge = document.createElement('div');
        this._queueBadge.className = 'pm-map-node__queue-badge';
        this._queueBadge.style.cssText = [
            'position:absolute',
            'right:6px',
            'bottom:6px',
            'padding:2px 8px',
            'border-radius:10px',
            'font-size:0.65rem',
            'line-height:1.4',
            'z-index:2',
            'display:none',
        ].join(';');
        this._chrome.mapContainer.appendChild(this._queueBadge);
        this._queuePollScheduled = false;
        this._onQueueBadgeClick = () => this._retryFromBadge();
        this._onQueueBadgeKeydown = (e) => {
            if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                this._retryFromBadge();
            }
        };
        this._queueBadge.addEventListener('click', this._onQueueBadgeClick);
        this._queueBadge.addEventListener('keydown', this._onQueueBadgeKeydown);

        this.dom = this._chrome.dom;
        this.contentDOM = null;

        this._chrome.rememberAttrs(node.attrs);
        this._chrome.updateChrome(node.attrs, node.attrs.viewId, []);

        // ─── Visibility/layout observers (ticket 11: consolidated "why two" explanation) ───────
        //
        // Two independent observers exist here because they detect two genuinely different
        // "this node might need a fresh preview" events, and neither one covers the other's case:
        //
        //   • IntersectionObserver (`_observer`, below) is the standard visibility signal: fires
        //     when the node scrolls into/out of the viewport. This is the common case (most map
        //     embeds are visible immediately or become visible via scrolling), and its handler
        //     does the "full" visible-refresh: invalidate the fragment cache (`_store.invalidate`)
        //     in case content changed while off-screen, then `_scheduleRefresh(true)` (which
        //     re-syncs the chrome dropdown/legend *and* enqueues the preview job — see
        //     `_refreshChrome`).
        //
        //   • ResizeObserver (`_resizeObserver`) exists for a gap IntersectionObserver alone can't
        //     see: a container that has *zero area* at mount (collapsed accordion, hidden tab,
        //     pre-layout) reports an intersection ratio of 0 regardless of whether it's
        //     "visible" in the scroll sense, so a `threshold:0` IntersectionObserver on it can
        //     report `isIntersecting: false` indefinitely — the node would never get a preview at
        //     all. This observer catches the zero-to-nonzero-size transition (e.g. the accordion
        //     expanding) as a *layout* event distinct from a *scroll* event, and — paired with the
        //     unconditional initial `_requestPreviewRefresh` call at the bottom of this
        //     constructor — closes that gap. Its handler intentionally stays minimal (just
        //     enqueues a priority refresh directly) rather than going through the same
        //     cache-invalidate + chrome-resync path as the IntersectionObserver handler, since a
        //     zero-size container has nothing rendered/cached yet to invalidate.
        //
        // Do not remove either without adding a regression test for the case it covers — removing
        // the ResizeObserver reintroduces the "stuck on placeholder forever in a collapsed
        // container" bug this pairing was added to fix (see MapNodeView.previewQueue.test.js's
        // "forces a refresh request when ResizeObserver reports the container growing from zero
        // size" case for that regression test).
        this._resizeObserver = new ResizeObserver((entries) => {
            if (this._destroyed) return;
            const rect = entries[0]?.contentRect;
            const hasSize = !!rect && rect.width > 0 && rect.height > 0;
            if (hasSize && !this._lastObservedNonZeroSize && !this._visible) {
                this._requestPreviewRefresh({ priority: true, fitBounds: true });
            }
            this._lastObservedNonZeroSize = hasSize;
        });
        this._resizeObserver.observe(this._chrome.mapContainer);

        this._observer = new IntersectionObserver((entries) => {
            if (this._destroyed) return;
            if (entries[0].isIntersecting) {
                // Standard scroll-visibility case — see the consolidated observer comment above.
                this._visible = true;
                const { nodeId } = this.node.attrs;
                if (nodeId) this._store.invalidate(nodeId);
                this._scheduleRefresh(true);
            } else {
                this._visible = false;
            }
        }, { threshold: 0 });
        this._observer.observe(this.dom);

        // Open the editor on double-click anywhere in the node.
        this._onDblClick = (e) => {
            if (this._chrome.containsTarget(e.target)) return;
            if (this._queueBadge.contains(e.target)) return; // ticket 05: don't open the editor when double-clicking the retry badge
            e.preventDefault();
            e.stopPropagation();
            this._openEditor();
        };
        this.dom.addEventListener('dblclick', this._onDblClick);

        const { nodeId } = node.attrs;
        if (nodeId) {
            this._unsubscribeFragment = this._store.subscribe(nodeId, () => {
                this._scheduleRefresh(false);
            });
            this._subscribePreview(nodeId);
            // Kick off an initial refresh request unconditionally (background-priority unless
            // already visible) so a node's preview never depends solely on IntersectionObserver
            // firing — see the stuck-invisible note above.
            this._requestPreviewRefresh({ priority: this._visible, fitBounds: true });
        }
    }

    // ─── Preview snapshot overlay ──────────────────────────────────────────────

    /**
     * Subscribe to preview changes for the given nodeId.
     * Mirrors FigureNodeView._subscribeAnnotation().
     */
    _subscribePreview(nodeId) {
        if (this._unobservePreview) {
            this._unobservePreview();
            this._unobservePreview = null;
        }

        const ymp = this._yMapPreviews;
        if (!ymp) return;

        // Initial render from currently stored preview PNG
        const previewUrl = getMapPreview(ymp, nodeId);
        this._setSnapshotSrc(previewUrl);

        // Live updates (e.g. when another collaborator saves, or our own queued job persists)
        this._unobservePreview = observeMapPreview(ymp, nodeId, () => {
            const updated = getMapPreview(ymp, nodeId);
            this._setSnapshotSrc(updated || '');
        });
    }

    /**
     * Set the snapshot overlay src. When a URL is provided the image covers the
     * placeholder; when cleared the placeholder is shown again.
     * Mirrors FigureNodeView._setOverlaySrc().
     */
    _setSnapshotSrc(url) {
        const img = this._snapshotImg;
        if (!img) return;
        if (url) {
            img.src = url;
            img.style.display = '';
            if (this._placeholder) this._placeholder.style.display = 'none';
        } else {
            img.src = '';
            img.style.display = 'none';
            if (this._placeholder) this._placeholder.style.display = '';
        }
    }

    /**
     * Ticket 07: give the snapshot `<img>` a meaningful `alt` instead of `""`, so screen readers
     * get something even though the map itself is a purely visual overlay (the underlying
     * geodata/markings aren't separately exposed in any accessible-text form here). Derives from
     * the node's caption when the reporter has written one (that's the one piece of
     * human-authored context this embed actually has); falls back to a generic description
     * otherwise. Called from the constructor and from `update()` wherever caption changes.
     */
    _updateSnapshotAlt(caption) {
        if (!this._snapshotImg) return;
        this._snapshotImg.alt = caption ? `Map preview: ${caption}` : 'Map preview';
    }

    // ─── Refresh helpers ───────────────────────────────────────────────────────

    _scheduleRefresh(fitBounds) {
        this._pendingFitBounds = this._pendingFitBounds || fitBounds;
        if (this._refreshScheduled) return;
        this._refreshScheduled = true;
        requestAnimationFrame(() => {
            this._refreshScheduled = false;
            const shouldFit = this._pendingFitBounds;
            this._pendingFitBounds = false;
            if (!this._destroyed) {
                this._refreshChrome(shouldFit);
            }
        });
    }

    /**
     * Update chrome (title, view dropdown, legend) from the current fragment state, and enqueue a
     * preview render/capture/persist job with the shared queue service. The actual OL sync +
     * snapshot capture + Yjs write happens inside MapPreviewQueueService, not here.
     */
    _refreshChrome(fitBounds = false) {
        if (this._destroyed) return;

        const { nodeId } = this.node.attrs;
        if (!nodeId) return;

        const previewState = this._store.getState(nodeId);
        const effectiveViewId = resolveActiveViewId(this.node.attrs);
        const displayViewId = resolveDisplayViewId(this.node.attrs);
        this._chrome.rememberAttrs(this.node.attrs);
        this._chrome.updateChrome(this.node.attrs, displayViewId, previewState.views, previewState.markings);

        // Bug fix (all-markings-dtg-crop): forward this embed's selected DTG so the "All
        // markings" fallback bbox resolves each marking's position AT this DTG (see
        // resolveViewBbox/computeDefaultViewBbox's doc comments) instead of each marking's
        // latest-ever position, matching the DTG used below for the actual rendered features.
        const bbox = resolveViewBbox(
            effectiveViewId,
            previewState.views,
            previewState.markings,
            previewState.layers,
            this.node.attrs?.dtgSelectedMs ?? null,
        );

        const fitKey = viewFitKey(effectiveViewId, bbox);
        const shouldFit = fitBounds || this._lastFitKey !== fitKey;
        if (shouldFit) {
            this._lastFitKey = fitKey;
        }

        this._requestPreviewRefresh({ priority: this._visible, fitBounds: shouldFit });
    }

    _requestPreviewRefresh({ priority = false, fitBounds = false } = {}) {
        const { nodeId } = this.node.attrs;
        if (!nodeId || !this._previewQueue) return;
        this._previewQueue.requestRefresh({
            nodeId,
            priority,
            fitBounds,
            getFragment: this._getFragment,
            getAttrs: () => this.node.attrs,
            ydoc: this._ydoc,
            yMapPreviews: this._yMapPreviews,
        });
        this._scheduleQueueStatePoll();
    }

    // ─── Queue-pending badge (ticket 10) ────────────────────────────────────────

    /**
     * Self-rescheduling requestAnimationFrame poll of `MapPreviewQueueService.getJobState()` —
     * mirrors `_scheduleRefresh`'s existing rAF-driven pattern rather than introducing a second,
     * event-based notification mechanism into the (currently plain, non-event-emitter) queue
     * service for this one query. Only keeps polling while this node has a queued/active job;
     * idles (no rAF loop running) once state goes back to null.
     */
    _scheduleQueueStatePoll() {
        if (this._queuePollScheduled || this._destroyed) return;
        this._queuePollScheduled = true;
        requestAnimationFrame(() => {
            this._queuePollScheduled = false;
            if (this._destroyed) return;
            this._updateQueueBadge();
        });
    }

    _updateQueueBadge() {
        const { nodeId } = this.node.attrs;
        const state = (nodeId && this._previewQueue?.getJobState)
            ? this._previewQueue.getJobState(nodeId)
            : null;
        if (state === 'queued') {
            this._queueBadge.textContent = 'Queued…';
            this._setQueueBadgeVariant('pending');
        } else if (state === 'active') {
            this._queueBadge.textContent = 'Rendering…';
            this._setQueueBadgeVariant('pending');
        } else if (state === 'error') {
            // Ticket 05: distinct from "Queued…"/"Rendering…" in both text and color, and — unlike
            // those transient states — clickable/focusable so the user has a manual retry
            // affordance instead of the placeholder just hanging forever with no feedback.
            this._queueBadge.textContent = 'Preview failed — click to retry';
            const detail = this._previewQueue?.getJobError?.(nodeId);
            if (detail) this._queueBadge.title = detail;
            this._setQueueBadgeVariant('error');
        } else {
            this._setQueueBadgeVariant('hidden');
        }
        if (state === 'queued' || state === 'active') this._scheduleQueueStatePoll();
    }

    /**
     * Applies the visual/interactive styling for one of the `_queueBadge`'s three variants.
     * Kept as one small switch rather than three near-duplicate style blocks inline in
     * `_updateQueueBadge`, so the "what makes 'error' different from 'pending'" answer (color +
     * clickability) lives in exactly one place.
     */
    _setQueueBadgeVariant(variant) {
        const badge = this._queueBadge;
        badge.dataset.variant = variant;
        if (variant === 'hidden') {
            badge.style.display = 'none';
            badge.style.pointerEvents = 'none';
            badge.removeAttribute('role');
            badge.removeAttribute('tabindex');
            return;
        }
        badge.style.display = '';
        if (variant === 'error') {
            badge.style.background = 'rgba(127,29,29,0.85)';
            badge.style.color = '#fff';
            badge.style.cursor = 'pointer';
            badge.style.pointerEvents = 'auto';
            badge.setAttribute('role', 'button');
            badge.setAttribute('tabindex', '0');
            badge.setAttribute('aria-label', 'Map preview failed to render. Click to retry.');
        } else {
            badge.style.background = 'rgba(15,23,42,0.72)';
            badge.style.color = '#fff';
            badge.style.cursor = '';
            badge.style.pointerEvents = 'none';
            badge.removeAttribute('role');
            badge.removeAttribute('tabindex');
            badge.removeAttribute('aria-label');
            badge.removeAttribute('title');
        }
    }

    /** Manual retry affordance (ticket 05) — only meaningful while the badge is in its error variant. */
    _retryFromBadge() {
        if (this._queueBadge.dataset.variant !== 'error') return;
        this._requestPreviewRefresh({ priority: true, fitBounds: false });
    }

    // ─── Situation report (MINSKY-16407 → MINSKY-16409 → MINSKY-16410) ───────────

    /**
     * Builds a curated JSON payload from THIS embed's current view (bbox) + selected DTG and
     * queues it as a situation-report generation request in the shared Yjs doc's
     * `Y.Map("sitrepGenerations")` (see sitrepGenerationsYMap.js) — mirroring the
     * agentic-section feature's request/observer/handler pattern rather than calling any LLM
     * directly from the client. ws-collaboration's SitrepGenerationObserver picks up the queued
     * entry, a SitrepGenerationHandler claims it, streams the generated markdown back into the
     * SAME entry (status queued→generating→done|error), and — once `done` — splices that
     * Markdown into the report's own document as AI-authored suggestion marks (MINSKY-16410;
     * see ws-collaboration's `sitrepSuggestionInsertion.js`), all server-side. `_subscribeSitrepGeneration`
     * below drives the under-the-map progress UI (`_renderSitrepProgress`) from that same entry;
     * the actual user-facing *result* once `done` is the normal suggestion-accept/reject UI
     * elsewhere in the doc, not anything rendered here.
     *
     * MINSKY-16404 point 7: `buildSitrepPayload` returns `{payload, images}` — the map preview
     * data URL(s) travel alongside the payload (not embedded in it) into the queued request's
     * `images` field, best-effort only (see MINSKY-16408's dash-api DTO doc comment for why the
     * generation endpoint does not require them).
     *
     * @param {{additionalContext: string, windowDays: number, maxCheckpoints: number|null}} params
     */
    async _onGenerateSitrep({additionalContext, windowDays, maxCheckpoints} = {}) {
        const { nodeId } = this.node.attrs;

        if (!this._ydoc || !this._yMapSitrepGenerations) {
            // No Yjs plumbing available (e.g. an older caller/test harness) — nothing to queue,
            // and nothing to show progress for.
            return;
        }

        // MINSKY-16410 points 1 & 5: cache the submitted inputs for Retry, disable the header
        // trigger, and put up the "queued" progress state immediately — all *before* the (up to
        // SITREP_PREVIEW_WAIT_MS) preview-capture wait and payload build below — so there is no
        // blank gap between clicking Generate and seeing feedback (the bug report this fixes),
        // and a second click can't kick off a second, concurrent generation for this node while
        // this one is still in flight. See `_setSitrepInFlight`'s doc comment for why "disable"
        // was chosen over "queue/replace" for that second case.
        this._lastSitrepParams = { additionalContext, windowDays, maxCheckpoints };
        this._setSitrepInFlight(true);
        this._renderSitrepProgress({ status: SITREP_GENERATION_STATUS.QUEUED });

        const previewState = this._store.getState(nodeId);
        const effectiveViewId = resolveActiveViewId(this.node.attrs);
        // Bug fix (all-markings-dtg-crop): same fix as `_refreshChrome` above — when this embed's
        // active view is "All markings", the sitrep payload's bbox scoping must reflect markings'
        // positions at the selected DTG, not their all-time historical union (see
        // resolveViewBbox/computeDefaultViewBbox's doc comments).
        const bbox = resolveViewBbox(
            effectiveViewId,
            previewState.views,
            previewState.markings,
            previewState.layers,
            this.node.attrs?.dtgSelectedMs ?? null,
        );

        const previewDataUrl = await this._resolvePreviewForSitrep(nodeId);

        // Best-effort reverse-geocode of the viewport centre so the LLM can describe
        // unit positions relative to known place names (country, city, etc.). A failed
        // or timed-out geocode produces null; the payload's `viewport` block is then
        // simply omitted and the LLM falls back to MGRS/lat-lon only.
        const center = bboxCenter(bbox);
        const viewport = center ? await reverseGeocode(center.lat, center.lon) : null;

        const { payload, images } = buildSitrepPayload({
            markings: previewState.markings,
            layers: previewState.layers,
            bbox,
            selectedDtg: this.node.attrs.dtgSelectedMs,
            windowDays,
            maxCheckpoints,
            additionalContext,
            previewDataUrl,
            viewport,
        });

        // Per-unit reverse-geocoding: resolve each visible unit's lat/lon to a named
        // location string (e.g. "Sloviansk, Donetsk Oblast, Ukraine") and attach it as
        // `locationName` so the LLM reads an exact place name rather than inferring one
        // from raw coordinates — the primary fix for units being attributed to the wrong
        // city. Rate-limited to 1 req/s by geocodeUnits; capped at 6 units to keep
        // total added latency ≤ ~7 s, hidden inside the existing "queued" wait state.
        const locationNames = await geocodeUnits(payload.units ?? []);
        for (const unit of (payload.units ?? [])) {
            const name = locationNames.get(unit.ref);
            if (name) unit.locationName = name;
        }

        const requestId = generateUUID();
        queueSitrepGeneration(this._ydoc, this._yMapSitrepGenerations, requestId, {
            nodeId,
            payload,
            images,
        });
        this._subscribeSitrepGeneration(requestId);
    }

    /**
     * MINSKY-16410 point 5: toggles the header sitrep trigger's disabled state for the duration
     * of one in-flight generation. "Disable" was chosen over "queue/replace" for a second submit
     * on the same node while one is already in flight: replacing would silently discard the
     * first request's payload (a snapshot of the map at an earlier moment) with no record of
     * that happening, and the server-side ownership split (one client-written entry per
     * requestId, everything past that server-owned — see sitrepGenerationsYMap.js's header
     * comment) doesn't anticipate two simultaneously-in-flight requests for one node. Disabling
     * is the simpler, safer choice for a per-node action that already has a Retry path once the
     * in-flight one reaches a terminal state (`done`/`error`).
     * @param {boolean} inFlight
     */
    _setSitrepInFlight(inFlight) {
        this._sitrepInFlight = inFlight;
        this._chrome.updateSitrepInFlight(inFlight);
    }

    /**
     * Re-renders the under-the-map progress/result UI (MapNodeChrome.renderSitrepProgress →
     * SitrepGenerationProgress.jsx) from a `sitrepGenerations` entry — or a synthetic
     * `{status: 'queued'}` seed used immediately on submit, before the real Yjs entry exists yet
     * (see `_onGenerateSitrep`) — via the pure `getSitrepProgressViewState` mapping.
     * @param {{status: string, errorMessage?: string}|null} generation
     */
    _renderSitrepProgress(generation) {
        const viewState = getSitrepProgressViewState(generation);
        this._chrome.renderSitrepProgress(viewState, {
            onRetry: () => this._retrySitrepGeneration(),
            retryPending: this._sitrepInFlight,
            onDismissSuccess: () => this._renderSitrepProgress(null),
        });
    }

    /**
     * MINSKY-16410 point 4: re-submits the last submitted inputs (cached in `_onGenerateSitrep`)
     * without reopening the modal or requiring the user to retype their additional-context text.
     * Only meaningful once the previous generation has reached a terminal state — the header
     * trigger being disabled already prevents this from racing an in-flight generation via the
     * normal UI, this is a defensive guard against the same race for this direct call path.
     */
    _retrySitrepGeneration() {
        if (!this._lastSitrepParams || this._sitrepInFlight) return;
        this._onGenerateSitrep(this._lastSitrepParams);
    }

    /**
     * Observes one queued generation request and drives the under-the-map progress UI as the
     * server streams status updates into it, unsubscribing — and re-enabling the header trigger
     * — once it reaches a terminal state (`done`/`error`). See `_onGenerateSitrep`'s doc comment
     * above for the full lifecycle this is driving.
     * @param {string} requestId
     */
    _subscribeSitrepGeneration(requestId) {
        const ymg = this._yMapSitrepGenerations;
        if (!ymg) return;

        const handleUpdate = () => {
            const generation = getSitrepGeneration(ymg, requestId);
            if (!generation) return;
            if (
                generation.status === SITREP_GENERATION_STATUS.DONE
                || generation.status === SITREP_GENERATION_STATUS.ERROR
            ) {
                // Flip in-flight off *before* rendering, so the freshly-rendered error state's
                // Retry button isn't born disabled (retryPending mirrors `_sitrepInFlight`).
                this._setSitrepInFlight(false);
                this._renderSitrepProgress(generation);
                unsubscribe();
            } else {
                this._renderSitrepProgress(generation);
            }
        };

        const unsubscribe = observeSitrepGeneration(ymg, requestId, handleUpdate);
        this._sitrepUnsubscribes = this._sitrepUnsubscribes || [];
        this._sitrepUnsubscribes.push(unsubscribe);
        handleUpdate();
    }

    /**
     * Reads the current preview snapshot for `nodeId`; if none exists yet, requests a fresh
     * capture and waits (bounded by `SITREP_PREVIEW_WAIT_MS`) for it to land in Yjs before giving
     * up and proceeding without one — see the module-level constant's doc comment.
     * @returns {Promise<string>}
     */
    _resolvePreviewForSitrep(nodeId) {
        if (!nodeId || !this._yMapPreviews) return Promise.resolve('');
        const existing = getMapPreview(this._yMapPreviews, nodeId);
        if (existing) return Promise.resolve(existing);
        if (!this._previewQueue) return Promise.resolve('');

        this._requestPreviewRefresh({ priority: true, fitBounds: false });

        return new Promise((resolve) => {
            let settled = false;
            const finish = (value) => {
                if (settled) return;
                settled = true;
                unobserve?.();
                clearTimeout(timeoutId);
                resolve(value || '');
            };
            const unobserve = observeMapPreview(this._yMapPreviews, nodeId, () => {
                finish(getMapPreview(this._yMapPreviews, nodeId));
            });
            const timeoutId = setTimeout(() => finish(''), SITREP_PREVIEW_WAIT_MS);
        });
    }

    _openEditor() {
        const pos = this.getPos();
        if (pos == null) return;
        const {
            nodeId, viewId, dtgSelectedMs, title, basemapStyleId, activeGridId, overlayType,
        } = this.node.attrs;
        this._onOpenEditor({
            nodeId,
            viewId: viewId ?? null,
            dtgSelectedMs: dtgSelectedMs ?? null,
            title,
            basemapStyleId,
            activeGridId,
            overlayType,
            // Ticket 03: one-shot flush-back — invoked by MapEditorPortal exactly once, on close,
            // with the modal's final selected DTG point. `getPos()`/`this.node` are read fresh at
            // invocation time (same pattern as `_onDtgSelected`), so this stays correct even if
            // the doc changed underneath while the modal was open; if this node view was since
            // destroyed/detached, `getPos()` returns null and the write is skipped.
            // shared-map-data ticket 02: the DTG *range* is no longer part of this round-trip —
            // it lives on the shared map doc's map_meta node and is read/written live by
            // MapEditorPortal/MapEditorModal directly (see mapSchema.js).
            onDtgRangeCommit: (dtg) => this._onDtgRangeCommit(dtg),
            // shared-map-data ticket 03: live write-back for basemapStyleId/activeGridId/
            // overlayType/currentDtgMs/title — called on every change (not just close), same
            // "feels live" contract as the embed chrome's own basemap quick-switch
            // (`_onBasemapSelected`, which now just calls this with a one-key patch).
            onWindowSettingsChange: (patch) => this._onWindowSettingsChange(patch),
        });
    }

    _onViewSelected(viewId) {
        const pos = this.getPos();
        if (pos == null) return;
        const tr = this.view.state.tr.setNodeMarkup(pos, null, {
            ...this.node.attrs,
            viewId,
        });
        this.view.dispatch(tr);
    }

    _onDtgSelected(dtgSelectedMs) {
        const pos = this.getPos();
        if (pos == null) return;
        const tr = this.view.state.tr.setNodeMarkup(pos, null, {
            ...this.node.attrs,
            dtgSelectedMs,
        });
        this.view.dispatch(tr);
    }

    /**
     * Ticket 08: per-node basemap control. shared-map-data ticket 03: `basemapStyleId` moved from
     * the shared map's `map_meta` Yjs node onto this outer mapNode's own attr (same storage
     * location as `viewId`/`dtgSelectedMs` — see `_onViewSelected`/`_onDtgSelected` above), so
     * this is now just a one-key case of the same write-back `_onWindowSettingsChange` uses for
     * the full-screen editor's basemap/grid/overlay/title/satellite-cursor controls.
     */
    _onBasemapSelected(basemapStyleId) {
        this._onWindowSettingsChange({ basemapStyleId });
    }

    /**
     * shared-map-data ticket 03: live write-back for the full-screen editor's per-embed "window"
     * settings — `basemapStyleId`/`activeGridId`/`overlayType`/`title`/`currentDtgMs`. Called on
     * every change (not just on close, unlike `_onDtgRangeCommit`) since these — like the embed
     * chrome's own basemap quick-switch this replaces — read as live, immediate edits rather
     * than a "commit on close" gesture; `currentDtgMs` in particular is already throttled
     * upstream (useSatelliteImagery.js), so no further debouncing is needed here.
     * @param {object} patch — any subset of the five window-setting attrs.
     */
    _onWindowSettingsChange(patch) {
        const pos = this.getPos();
        if (pos == null) return;
        this.view.dispatch(
            this.view.state.tr.setNodeMarkup(pos, null, { ...this.node.attrs, ...patch }),
        );
    }

    /**
     * Ticket 03: one-shot write-back of the full-screen editor's final scrub point onto this
     * node's `dtgSelectedMs` attr, called by MapEditorPortal exactly once when the modal closes
     * (mirrors `_onDtgSelected`).
     * shared-map-data ticket 02: previously also wrote `dtgStartMs`/`dtgEndMs` here — that range
     * now lives on the shared map doc's `map_meta` node, written live by MapEditorPortal's
     * existing PM-doc → Yjs sync, so there's nothing left for this callback to do for the range.
     * @param {{ valueMs: number|null }} [dtg]
     */
    _onDtgRangeCommit(dtg = {}) {
        const pos = this.getPos();
        if (pos == null) return;
        const { valueMs = null } = dtg;
        const tr = this.view.state.tr.setNodeMarkup(pos, null, {
            ...this.node.attrs,
            dtgSelectedMs: valueMs,
        });
        this.view.dispatch(tr);
    }

    _onClassificationChange(result) {
        const pos = this.getPos();
        if (pos == null) return;
        this.view.dispatch(
            this.view.state.tr.setNodeMarkup(pos, null, { ...this.node.attrs, ...result }),
        );
    }

    _onCaptionChange(caption) {
        if (caption === (this.node.attrs.caption || '')) return;
        const pos = this.getPos();
        if (pos == null) return;
        this.view.dispatch(
            this.view.state.tr.setNodeMarkup(pos, null, { ...this.node.attrs, caption }),
        );
    }

    update(node) {
        if (node.type !== this.node.type) return false;
        const prev = this.node;
        this.node = node;
        this._chrome.rememberAttrs(node.attrs);
        this._chrome.applyClassification(node.attrs);
        if (prev.attrs.caption !== node.attrs.caption) {
            this._chrome.updateCaption(node.attrs.caption);
            this._updateSnapshotAlt(node.attrs.caption);
        }

        if (prev.attrs.dtgSelectedMs !== node.attrs.dtgSelectedMs) {
            const { nodeId } = node.attrs;
            const previewState = nodeId ? this._store.getState(nodeId) : null;
            this._chrome.updateDtg(node.attrs, previewState?.markings || []);
        }

        // shared-map-data ticket 03: basemapStyleId/activeGridId/overlayType/currentDtgMs/title
        // are now per-node "window" attrs (moved off the shared map's `map_meta`) — a change to
        // any of them needs the same "resync chrome + enqueue a fresh preview render" treatment
        // as dtgSelectedMs above (see the ticket-06 comment further down for why a preview
        // refresh, not just a chrome repaint, is required). None of them affect the view's
        // bbox/fit, so fitBounds stays false.
        if (
            prev.attrs.basemapStyleId !== node.attrs.basemapStyleId ||
            prev.attrs.activeGridId !== node.attrs.activeGridId ||
            prev.attrs.overlayType !== node.attrs.overlayType ||
            prev.attrs.currentDtgMs !== node.attrs.currentDtgMs ||
            prev.attrs.title !== node.attrs.title
        ) {
            this._chrome.updateBasemapSelect(node.attrs.basemapStyleId);
            this._scheduleRefresh(false);
        }

        if (prev.attrs.nodeId !== node.attrs.nodeId) {
            // nodeId changed — re-subscribe to preview for the new id, and cancel any pending
            // queue job for the old id (it no longer corresponds to this view).
            if (prev.attrs.nodeId) this._previewQueue?.cancel(prev.attrs.nodeId);
            if (node.attrs.nodeId) {
                this._subscribePreview(node.attrs.nodeId);
                this._unsubscribeFragment?.();
                this._unsubscribeFragment = this._store.subscribe(node.attrs.nodeId, () => {
                    this._scheduleRefresh(false);
                });
                this._requestPreviewRefresh({ priority: this._visible, fitBounds: true });
            } else {
                if (this._unobservePreview) {
                    this._unobservePreview();
                    this._unobservePreview = null;
                }
                this._setSnapshotSrc('');
            }
        }

        if (prev.attrs.viewId !== node.attrs.viewId) {
            this._lastFitKey = undefined;
            this._scheduleRefresh(true);
        }

        // Ticket 06: dtgSelectedMs alone (unlike viewId) never changes the view's bbox/fit, but it
        // *does* change which marking positions/state get rendered (see
        // MapPreviewQueueService._runJob's dtgSelectedMs-aware signature/markingsToGeoJSON call),
        // so a DTG-only change still needs to enqueue a real preview refresh — not just repaint
        // the chrome's date input above. Previously this branch was missing entirely, so
        // selecting a new DTG updated the dropdown/input immediately but silently left the
        // snapshot stale forever (no `_requestPreviewRefresh` call on this path at all, so the
        // ticket-10 queue badge never had anything to show either). fitBounds stays false here
        // since the bbox/view fit is unaffected.
        if (prev.attrs.dtgSelectedMs !== node.attrs.dtgSelectedMs) {
            this._scheduleRefresh(false);
        }
        return true;
    }

    selectNode() {
        this.dom.classList.add('ProseMirror-selectednode');
    }

    deselectNode() {
        this.dom.classList.remove('ProseMirror-selectednode');
    }

    stopEvent(event) {
        if (this._chrome.containsTarget(event.target)) return true;
        if (event.type === 'mousedown') return false;
        if (event.type === 'dblclick') return false;
        return true;
    }

    ignoreMutation() {
        return true;
    }

    destroy() {
        this._destroyed = true;
        this._observer?.disconnect();
        this._resizeObserver?.disconnect();
        this._unsubscribeFragment?.();
        if (this._unobservePreview) {
            this._unobservePreview();
            this._unobservePreview = null;
        }
        if (this._sitrepUnsubscribes) {
            this._sitrepUnsubscribes.forEach((unsub) => unsub());
            this._sitrepUnsubscribes = null;
        }
        if (this._onDblClick) {
            this.dom.removeEventListener('dblclick', this._onDblClick);
            this._onDblClick = null;
        }
        if (this._onQueueBadgeClick) {
            this._queueBadge.removeEventListener('click', this._onQueueBadgeClick);
            this._queueBadge.removeEventListener('keydown', this._onQueueBadgeKeydown);
            this._onQueueBadgeClick = null;
            this._onQueueBadgeKeydown = null;
        }
        this._store.destroy();
        const { nodeId } = this.node.attrs;
        if (nodeId) this._previewQueue?.cancel(nodeId);
        this._chrome.destroy();
    }
}
